/* Workspace — Files / Vault
 *
 *  Endpoints:
 *    GET    /api/files/tree                          → nested directory tree
 *    GET    /api/files/list?path=                    → flat list
 *    GET    /api/files/read?path=                    → text / image / pdf inline preview
 *    GET    /api/files/download?path=&token=         → binary download
 *    POST   /api/files/upload                        multipart: file, path
 *    POST   /api/files/mkdir                         { path }
 *    POST   /api/files/rename                        { old_path, new_path }
 *    DELETE /api/files?path=
 *    PUT    /api/files/classify                      { path, classification }
 *    GET    /api/files/search?q=&classification=
 */

/* Render-time function, not a module-level const: HR colours are var(--gx-*) under Expressive
 * and restore() must bring Bento back (SPEC §1.2「內頁換膚」, tests/test_expressive_pages.cjs §7). */
function fileClassifications() {
  return [
    { code: 'public',       label: '公開',   color: HR.accent, dot: '#0B5F5A' },
    { code: 'internal',     label: '內部',   color: HR.inkSoft, dot: '#8B8F89' },
    { code: 'confidential', label: '機密',   color: HR.warn,   dot: '#A56A1F' },
    { code: 'restricted',   label: '限制',   color: HR.down,   dot: '#9B3330' },
  ];
}
function classMeta(code) {
  return fileClassifications().find(c => c.code === code) || { code, label: code || '未分類', color: HR.inkFaint, dot: HR.inkFaint };
}
// Expressive：分類 → GxCore.tone 色調（tonal 底＋AA 前景；圓點維持原識別色）
const FILES_GX_CLASS_TONE = { public: 'accent', internal: 'neutral', confidential: 'warn', restricted: 'down' };

/* Expressive 外殼專屬：手機把三欄（目錄樹／列表／預覽）與 KPI 列改成單欄。No-op under Bento. */
GxCore.injectCss('gx-css-p-files', String.raw`
@media (max-width:1099px){
html[data-ui-shell="expressive"] .gx-files-cols{grid-template-columns:minmax(0,1fr) minmax(0,1fr)!important}
html[data-ui-shell="expressive"] .gx-files-cols > :first-child{grid-column:1 / -1}
}
@media (max-width:767px){
html[data-ui-shell="expressive"] .gx-files-cols{grid-template-columns:minmax(0,1fr)!important}
html[data-ui-shell="expressive"] .gx-files-kpi{gap:8px!important}
html[data-ui-shell="expressive"] .gx-files-search{flex-wrap:wrap}
}
`);

function FilesPage() {
  const toast = useToast();
  const [tree, setTree] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [cwd, setCwd] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [activePath, setActivePath] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [search, setSearch] = React.useState('');

  const loadTree = React.useCallback(() => {
    setLoading(true);
    api('GET', '/files/tree')
      .then(d => setTree(d))
      .catch(e => toast(e.detail || '檔案樹載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  const loadDir = React.useCallback((path) => {
    api('GET', `/files/list?path=${encodeURIComponent(path)}`)
      .then(d => setItems(d.items || []))
      .catch(e => toast(e.detail || '目錄載入失敗', 'error'));
  }, [toast]);

  React.useEffect(() => { loadTree(); }, [loadTree]);
  React.useEffect(() => { loadDir(cwd); }, [cwd, loadDir]);

  // Load preview when activePath set
  React.useEffect(() => {
    if (!activePath) { setPreview(null); return; }
    api('GET', `/files/read?path=${encodeURIComponent(activePath)}`)
      .then(d => setPreview(d))
      .catch(e => { setPreview({ error: e.detail || '預覽失敗' }); });
  }, [activePath]);

  // Search
  const [searchResults, setSearchResults] = React.useState(null);
  React.useEffect(() => {
    if (!search.trim()) { setSearchResults(null); return; }
    const t = setTimeout(() => {
      api('GET', `/files/search?q=${encodeURIComponent(search.trim())}`)
        .then(d => setSearchResults(d.results || []))
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const refresh = () => { loadTree(); loadDir(cwd); };

  // Breadcrumb segments from cwd
  const segments = cwd ? cwd.split('/').filter(Boolean) : [];

  // Stats
  const dirCount = items.filter(i => i.type === 'directory').length;
  const fileCount = items.filter(i => i.type === 'file').length;
  const totalSize = items.filter(i => i.type === 'file').reduce((s, i) => s + (i.size || 0), 0);

  return (
    <>
      <PageHeader
        eyebrow="工作空間 · Vault"
        title="文件知識庫"
        italic="File Vault"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <UploadButton cwd={cwd} onUploaded={refresh} />
            <MkdirButton cwd={cwd} onCreated={refresh} />
            <Button variant="ghost" icon="refresh" onClick={refresh}>更新</Button>
          </div>
        }
      />

      {/* Search bar */}
      <div className={GxCore.cls(undefined, 'gx-files-search')} style={uiStyle({
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18,
        padding: '10px 14px', background: HR.surface,
        border: `1px solid ${HR.rule}`, borderRadius: 6,
      }, { padding: '12px 16px', border: 'none', borderRadius: GxT.radius.card })}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: HR.inkFaint, pointerEvents: 'none' }}>
            <Icon name="search" size={13} />
          </span>
          <input
            value={search} placeholder="跨目錄搜尋檔名…" onChange={(e) => setSearch(e.target.value)}
            style={inputStyle({ paddingLeft: 32 })}
          />
        </div>
        {search && <Button size="sm" variant="ghost" onClick={() => setSearch('')}>清除</Button>}
      </div>

      {searchResults !== null ? (
        <Card padding="0" eyebrow="Search" title={`搜尋: "${search}"`} italic={`${searchResults.length} 個結果`}>
          {searchResults.length === 0 ? (
            <EmptyState icon="search" title="無符合結果" />
          ) : (
            <div style={{ maxHeight: 540, overflowY: 'auto' }}>
              {searchResults.map((it, i) => (
                <div key={i} onClick={() => setActivePath(it.path)}
                  className="hr-row"
                  style={{
                    display: 'grid', gridTemplateColumns: '32px 1fr 100px 80px', gap: 12,
                    padding: '11px 18px', borderTop: i > 0 ? `1px solid ${HR.ruleSoft}` : 'none',
                    cursor: 'pointer', alignItems: 'center',
                  }}>
                  <Icon name={iconForItem(it)} size={14} />
                  <div>
                    <div style={{ fontSize: FontSizes.xs, color: HR.ink }}>{it.name}</div>
                    <div style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, fontFamily: HR.mono }}>{it.path}</div>
                  </div>
                  <ClassPill code={it.classification} />
                  <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkSoft, fontFamily: HR.mono, textAlign: 'right' }}>{fmtSize(it.size)}</span>
                </div>
              ))}
            </div>
          )}
          <style>{`.hr-row:hover { background: ${HR.surfaceAlt}; }`}</style>
        </Card>
      ) : (
        <>
          {/* KPI strip */}
          <div className={GxCore.cls(undefined, 'gx-files-kpi')} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
            <KpiCard label="目錄" value={dirCount} />
            <KpiCard label="檔案" value={fileCount} />
            <KpiCard label="總大小" value={fmtSize(totalSize)} />
          </div>

          <div className={GxCore.cls(undefined, 'gx-files-cols')} style={{ display: 'grid', gridTemplateColumns: '260px 1fr 1fr', gap: 14 }}>
            {/* Tree */}
            <Card padding="0" eyebrow="Tree" title="目錄結構">
              {loading ? <SkeletonText lines={8} /> : tree ? (
                <div style={{ padding: 8, maxHeight: 560, overflowY: 'auto' }}>
                  <TreeNode node={tree} depth={0} cwd={cwd} onNavigate={setCwd} onPreview={setActivePath} />
                </div>
              ) : <EmptyState icon="folder" title="無檔案" />}
            </Card>

            {/* Directory listing */}
            <Card
              padding="0"
              eyebrow={cwd ? 'Folder' : 'Root'}
              title="目錄內容"
              italic={
                <span>
                  <a href="#" onClick={(e) => { e.preventDefault(); setCwd(''); }} style={{ color: HR.accent, textDecoration: 'none' }}>/</a>
                  {segments.map((s, i) => (
                    <React.Fragment key={i}>
                      {' / '}
                      <a href="#" onClick={(e) => { e.preventDefault(); setCwd(segments.slice(0, i + 1).join('/')); }}
                        style={{ color: HR.accent, textDecoration: 'none' }}>{s}</a>
                    </React.Fragment>
                  ))}
                </span>
              }
            >
              {items.length === 0 ? (
                <EmptyState icon="folder" title="空目錄" hint="上傳檔案或建新目錄" />
              ) : (
                <div style={{ maxHeight: 560, overflowY: 'auto' }}>
                  {items.map(it => (
                    <FileRow key={it.path} item={it}
                      isActive={activePath === it.path}
                      onClick={() => {
                        if (it.type === 'directory') setCwd(it.path);
                        else setActivePath(it.path);
                      }}
                      onDelete={async () => {
                        if (!confirm(`刪除「${it.name}」？此操作無法復原。`)) return;
                        try {
                          await api('DELETE', `/files?path=${encodeURIComponent(it.path)}`);
                          toast('已刪除', 'ok'); refresh();
                          if (activePath === it.path) setActivePath(null);
                        } catch (e) { toast(e.detail || '刪除失敗', 'error'); }
                      }}
                      onClassify={async (cls) => {
                        try {
                          await api('PUT', '/files/classify', { path: it.path, classification: cls });
                          toast('已分類', 'ok'); refresh();
                        } catch (e) { toast(e.detail || '失敗', 'error'); }
                      }}
                    />
                  ))}
                </div>
              )}
            </Card>

            {/* Preview */}
            <Card padding="0" eyebrow="Preview" title={activePath ? activePath.split('/').pop() : '預覽'} italic={activePath || '選擇右側檔案'}>
              {!activePath ? (
                <div style={{ padding: 30, textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs }}>
                  從中間列表選擇一個檔案
                </div>
              ) : preview ? <PreviewBody preview={preview} path={activePath} /> : <SkeletonText lines={6} />}
            </Card>
          </div>
        </>
      )}
    </>
  );
}

function KpiCard({ label, value }) {
  return (
    <div style={uiStyle({
      padding: '14px 18px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
    }, { padding: '16px 18px', background: GxT.color.card, border: 'none', borderRadius: GxT.radius.card })}>
      <div style={uiStyle({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase' },
        { fontSize: 13, fontWeight: 700, color: GxT.color.onV, letterSpacing: 0, textTransform: 'none' })}>{label}</div>
      <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: 500, marginTop: 4, color: HR.ink },
        { fontWeight: GxT.weight.title, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' })}>{value}</div>
    </div>
  );
}

function ClassPill({ code }) {
  const m = classMeta(code);
  const t = GxCore.tone(FILES_GX_CLASS_TONE[m.code] || 'neutral');
  return (
    <span style={uiStyle({
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 7px', fontSize: FontSizes.trading_sm, borderRadius: 10,
      background: GxCore.alpha(m.color, '15'), color: m.color,
      fontFamily: HR.mono, fontWeight: 500,
    }, {
      gap: 6, padding: '3px 8px', fontSize: 12, borderRadius: GxT.radius.chip,
      background: t.bg, color: t.fg, fontFamily: GxT.font, fontWeight: 700, whiteSpace: 'nowrap',
    })}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: m.dot }} />
      {m.label}
    </span>
  );
}

function iconForItem(it) {
  if (it.type === 'directory') return 'folder';
  const name = (it.name || '').toLowerCase();
  if (/\.(mp4|mov|webm|avi)$/.test(name)) return 'activity';
  if (/\.(md|txt)$/.test(name)) return 'file-text';
  if (/\.pdf$/.test(name)) return 'file-text';
  return 'file-text';
}

function fmtSize(bytes) {
  if (!bytes || bytes < 1024) return (bytes || 0) + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function TreeNode({ node, depth, cwd, onNavigate, onPreview }) {
  const [open, setOpen] = React.useState(depth < 2);
  const isDir = node.type === 'directory';
  const isCurrent = isDir && node.path === cwd;
  return (
    <>
      <div
        onClick={() => {
          if (isDir) { setOpen(!open); onNavigate(node.path || ''); }
          else onPreview(node.path);
        }}
        style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', paddingLeft: 8 + depth * 14,
          borderRadius: 3, cursor: 'pointer', fontSize: FontSizes.trading_md,
          background: isCurrent ? HR.accentSoft : 'transparent',
          color: isCurrent ? HR.accent : HR.ink,
          marginBottom: 1,
        }, {
          padding: '7px 10px', paddingLeft: 10 + depth * 14, minHeight: 34, borderRadius: GxT.radius.btnSm,
          fontSize: 13, background: isCurrent ? GxT.color.sc : 'transparent', color: isCurrent ? GxT.color.onSc : GxT.color.on,
          marginBottom: 2,
        })}
      >
        {isDir && (
          <span style={{ width: 10, color: HR.inkFaint }}>{open ? '▾' : '▸'}</span>
        )}
        {!isDir && <span style={{ width: 10 }} />}
        <Icon name={iconForItem(node)} size={11} />
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontWeight: isCurrent ? uiPick(500, 700) : uiPick(400, 500),
        }}>{node.name || '/'}</span>
      </div>
      {isDir && open && node.children && node.children.map(c => (
        <TreeNode key={c.path} node={c} depth={depth + 1} cwd={cwd} onNavigate={onNavigate} onPreview={onPreview} />
      ))}
    </>
  );
}

function FileRow({ item, isActive, onClick, onDelete, onClassify }) {
  const [menu, setMenu] = React.useState(false);
  return (
    <div
      className="hr-row"
      onClick={onClick}
      style={{
        display: 'grid', gridTemplateColumns: '24px 1fr 100px 70px 32px', gap: 10,
        padding: '11px 18px', cursor: 'pointer', alignItems: 'center',
        borderBottom: `1px solid ${HR.ruleSoft}`,
        background: isActive ? HR.surfaceAlt : 'transparent',
      }}
    >
      <Icon name={iconForItem(item)} size={13} />
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: FontSizes.xs, color: HR.ink, fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{item.name}</div>
        {item.modified && (
          <div style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, fontFamily: HR.mono }}>
            {fmtDateTime(item.modified)}
          </div>
        )}
      </div>
      {item.type === 'file' && <ClassPill code={item.classification} />}
      {item.type === 'file' && (
        <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkSoft, fontFamily: HR.mono, textAlign: 'right' }}>
          {fmtSize(item.size)}
        </span>
      )}
      {item.type === 'directory' && <span /> /* placeholder */}
      {item.type === 'directory' && <span />}
      <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setMenu(!menu)} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: HR.inkFaint, padding: 2,
        }}><Icon name="more" size={14} /></button>
        {menu && (
          <div style={uiStyle({
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 5,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 150, zIndex: 20,
            padding: 4,
          }, { border: 'none', borderRadius: GxT.radius.inner, boxShadow: GxT.shadow.pop, minWidth: 180, padding: 6 })}>
            {item.type === 'file' && fileClassifications().map(c => (
              <div key={c.code} onClick={() => { setMenu(false); onClassify(c.code); }}
                style={uiStyle({ padding: '7px 10px', fontSize: FontSizes.xxs, cursor: 'pointer', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 8 },
                  { padding: '10px 12px', fontSize: 13, fontWeight: 600, borderRadius: GxT.radius.btnSm })}
                onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.dot }} />
                <span style={{ color: c.color }}>分類為「{c.label}」</span>
              </div>
            ))}
            {item.type === 'file' && <div style={{ height: 1, background: HR.ruleSoft, margin: '4px 0' }} />}
            <div onClick={() => { setMenu(false); onDelete(); }}
              style={uiStyle({ padding: '7px 10px', fontSize: FontSizes.xxs, cursor: 'pointer', borderRadius: 3, color: HR.down },
                { padding: '10px 12px', fontSize: 13, fontWeight: 700, borderRadius: GxT.radius.btnSm })}
              onMouseEnter={(e) => e.currentTarget.style.background = uiPick('#F4DAD8', GxT.tint.down.bg)}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >刪除</div>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewBody({ preview, path }) {
  if (preview.error) {
    return (
      <div style={{ padding: 24, color: HR.down, fontSize: FontSizes.xs, textAlign: 'center' }}>
        ⚠ {preview.error}
      </div>
    );
  }
  if (preview.type === 'text') {
    return (
      <pre style={{
        margin: 0, padding: 14, background: HR.surfaceAlt,
        fontFamily: HR.serif, fontSize: FontSizes.xs, lineHeight: 1.65,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 540, overflowY: 'auto', color: HR.ink,
      }}>{preview.content}</pre>
    );
  }
  if (preview.type === 'image') {
    return (
      <div style={{ padding: 14 }}>
        <img src={preview.content} alt={preview.filename}
          style={{ maxWidth: '100%', maxHeight: 500, borderRadius: uiPick(4, GxT.radius.inner) }} />
      </div>
    );
  }
  if (preview.type === 'pdf') {
    return <iframe src={preview.content} style={{ width: '100%', height: 540, border: 'none' }} />;
  }
  // binary
  return (
    <div style={{ padding: 24, textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs }}>
      無法線上預覽（{fmtSize(preview.size)}）
      <div style={{ marginTop: 12 }}>
        <a href={`/api/files/download?path=${encodeURIComponent(path)}&token=${encodeURIComponent(getToken())}`}
          target="_blank" rel="noopener noreferrer"
          style={uiStyle({
            display: 'inline-block', padding: '6px 14px', fontSize: FontSizes.xxs,
            background: HR.accent, color: '#fff', borderRadius: 4, textDecoration: 'none',
          }, { padding: '10px 18px', fontSize: 14, fontWeight: 700, borderRadius: GxT.radius.btn })}>下載</a>
      </div>
    </div>
  );
}

function UploadButton({ cwd, onUploaded }) {
  const toast = useToast();
  const ref = React.useRef(null);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('path', cwd);
    try {
      const r = await fetch('/api/files/upload', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + getToken() },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw { detail: data.detail || `HTTP ${r.status}` };
      toast(`已上傳 ${data.filename}`, 'ok');
      onUploaded();
    } catch (err) { toast(err.detail || '上傳失敗', 'error'); }
    finally { e.target.value = ''; }
  };
  return (
    <>
      <input ref={ref} type="file" onChange={handle} style={{ display: 'none' }} />
      <Button variant="primary" icon="plus" onClick={() => ref.current?.click()}>上傳檔案</Button>
    </>
  );
}

function MkdirButton({ cwd, onCreated }) {
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const submit = async () => {
    if (!name.trim()) return;
    const newPath = cwd ? `${cwd}/${name.trim()}` : name.trim();
    try {
      await api('POST', '/files/mkdir', { path: newPath });
      toast('目錄已建立', 'ok');
      setOpen(false); setName('');
      onCreated();
    } catch (e) { toast(e.detail || '失敗', 'error'); }
  };
  return (
    <>
      <Button variant="secondary" icon="folder" onClick={() => setOpen(true)}>新目錄</Button>
      {open && (
        <Modal open onClose={() => setOpen(false)} title="新增目錄" subtitle={cwd || '/'} size="sm"
          footer={<>
            <Button variant="ghost" onClick={() => setOpen(false)}>取消</Button>
            <Button variant="primary" onClick={submit}>建立</Button>
          </>}>
          <Field label="目錄名稱" required>
            <TextInput value={name} onChange={setName} placeholder="e.g. reports" autoFocus />
          </Field>
        </Modal>
      )}
    </>
  );
}

window.FilesPage = FilesPage;
